import { POIShape } from './POIShape';
import * as THREE from 'three';
import { AppSettingsData } from '../core/AppSettingsData';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { ThreeGeomFactory } from './ThreeGeomFactory';
import { POI } from '../poi/POI';

export class HexagonShape extends POIShape {

    constructor(
        poi: POI,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData
    ) {
        super(poi, sceneSettings, appSettings, {});

        // Listen for geometry-affecting changes
        this.poi.poidata.cornerRadius?.on(() => this.recreateGeometry());
        this.poi.poidata.edgeRadius?.on(() => this.recreateGeometry());
        this.sceneSettings?.geometryResolution.on(() => this.recreateGeometry());
        
        // Material aspect ratio affects the geometry for proper UV mapping
        this.primaryMaterial.aspectRatio.on(() => this.recreateGeometry());
        this.poi.poidata.textureFit?.on(() => this.recreateGeometry());
    }

    protected override createGeometry(): THREE.BufferGeometry {
        // Base size and settings
        const size = 0.5; // Base circumradius
        const depth = this.poi.poidata.depth?.value ?? 0.1; // Fixed depth
        const cornerRadius = this.poi.poidata.cornerRadius?.value ?? 0.0;
        const edgeRadius = this.poi.poidata.edgeRadius?.value ?? 0.0;
        const resolution = this.sceneSettings?.geometryResolution.value ?? 1.0;
        
        // Get texture aspect ratio for proper UV mapping
        const aspectRatio = this.primaryMaterial.aspectRatio.value ?? null;
        
        const relativeCornerRadius = cornerRadius > 0 ? cornerRadius : 0;
        // Scale down edgeRadius for hexagons to prevent overly aggressive beveling
        const relativeEdgeRadius = edgeRadius > 0 ? (edgeRadius * 0.5) : 0;

        // Use beveled edges if edgeRadius is greater than 0
        if (relativeEdgeRadius > 0) {
            return ThreeGeomFactory.createSimpleHexagonWithBeveledEdges(
                size, 
                depth, 
                relativeCornerRadius, 
                relativeEdgeRadius, 
                resolution, 
                aspectRatio,
                this.poi.poidata.textureBacksideFlipX?.value ?? true,
                this.poi.poidata.useBacksideSideMaterial?.value ?? false
            );
        } else {
            return ThreeGeomFactory.createRoundedHexagon(size, depth, relativeCornerRadius, resolution, aspectRatio, this.poi.poidata.textureBacksideFlipX?.value ?? true, this.poi.poidata.useBacksideSideMaterial?.value ?? false, this.poi.poidata.textureFit?.value ?? 'cover');
        }
    }

    private getMaterialAspectRatio(): number | null {
        // Material is always available since it's created before geometry
        ////console.log(`📐 HexagonShape using aspect ratio: ${this.primaryMaterial.aspectRatio.value?.toFixed(3) ?? 'null'}`);
        return this.primaryMaterial.aspectRatio.value;
    }

    /**
     * The factory hardcodes group material indices (0=front, 1=back, 2=sides with index 1),
     * but Mesh3D builds the material array dynamically: [front, back?, sides?]. When both
     * back and sides exist, "sides" lives at index 2 — the factory's hardcoded 1 then points
     * at the back material (often the same video/texture as front), which smears stretched
     * texture stripes across the extruded side walls. Assign the correct dynamic indices here.
     */
    protected override assignMaterialGroupsToGeometry(materialSet: import('../materials/MaterialFactory').MaterialSet): void {
        if (!this._geometry || !this._geometry.groups || this._geometry.groups.length !== 3) return;

        const FRONT = 0;
        const BACK = materialSet.back ? 1 : -1;
        const SIDES = materialSet.sides ? (materialSet.back ? 2 : 1) : -1;
        const useBacksideSide = this.poi.poidata.useBacksideSideMaterial?.value ?? false;

        this._geometry.groups[0].materialIndex = FRONT;
        this._geometry.groups[1].materialIndex = useBacksideSide && SIDES >= 0
            ? SIDES
            : (BACK >= 0 ? BACK : FRONT);
        // Side walls must never sample the front texture — prefer sides, then back, then front.
        this._geometry.groups[2].materialIndex = SIDES >= 0 ? SIDES : (BACK >= 0 ? BACK : FRONT);
    }
}